// src/pages/email-funnel/verify-email.tsx (or wherever your VerifyEmail lives)
import { useState, useEffect } from 'react';
import { useTheme } from '../../../../contexts/theme-context';
import { authApi } from '../../../../api';
import { OnboardingStepProps } from '../../registration';
import { ContentSection, Heading, SubHeading } from '../../shared.styled';
import {
  EmailIcon,
  InstructionText,
  VerifyButton,
  ResendLink,
  TimerText,
  ErrorText,
} from './email-funnel.styled';
import { FloatingInput } from '../../../../components/floating-input/floating-input';
import { VSpace } from '../../../chat/settings/account/account-settings';
import { SkipButton } from '../secure-your-account/secure-your-account.styled';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { UniversalSpinner } from '../../../../components/universal-spinner/universal-spinner';

export const VerifyEmail: React.FC<OnboardingStepProps> = ({ goNext, goPrev }) => {
  const { isLight } = useTheme();
  const { onboarding, setRegistration } = useAuth();

  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Prefer pendingEmail, but if for any reason it wasn't set (prefilled + no typing),
  // fall back to onboarding.email so this screen is never "empty" / disabled.
  const targetEmail = (onboarding as any)?.pendingEmail ?? (onboarding as any)?.email ?? undefined;

  /* ───────── cooldown ticker ───────── */
  useEffect(() => {
    if (!secondsLeft) return;
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1_000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  /* ───────── verify the OTP ───────── */
  const handleVerify = async () => {
    const trimmed = code.trim();
    if (!trimmed || !targetEmail) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      /**
       * ✅ ONBOARDING OTP VERIFY
       * - Verify OTP for signup
       * - Does not create account yet (no session)
       */
      const invitationCode = (onboarding as any)?.invitationCode;
      await authApi.verifyOtp(targetEmail, trimmed, invitationCode);

      setRegistration({ email: targetEmail, pendingEmail: undefined } as any);

      goNext();
    } catch (error: any) {
      console.error('Verify error:', error);
      setErrorMsg(error.response?.data?.detail || 'Incorrect code – please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ───────── resend the OTP ───────── */
  const handleResend = async () => {
    if (secondsLeft || !targetEmail) return;
    try {
      const invitationCode = (onboarding as any)?.invitationCode;
      await authApi.resend_otp({ email: targetEmail, invitation_code: invitationCode });
      setSecondsLeft(30); // 30-second cooldown
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      setErrorMsg(
        error.response?.data?.detail || 'Failed to resend code. Please try again in a moment.'
      );
    }
  };

  const codeReady = code.trim().length === 6;
  const buttonsDisabled = loading || !targetEmail;

  return (
    <ContentSection data-testid="ob-emailfunnel">
      <div data-testid="ob-emailfunnel-headings">
        <VSpace $h={32} />
        <Heading isLight={isLight} data-testid="ob-signup-heading">
          Judgment. <br></br> Refined.{` `}
        </Heading>
        <VSpace $h={16} />

        <SubHeading isLight={isLight} data-testid="ob-signup-subheading">
          The long conversations that create enterprise value.
        </SubHeading>
        <VSpace $h={71} />
      </div>

      <div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
      >
        <EmailIcon
          src="/icons/check-email.png"
          alt="Email sent"
          data-testid="ob-emailfunnel-icon"
        />
        <VSpace $h={16} />
        <InstructionText data-testid="ob-emailfunnel-instruction">
          Enter the code sent to <br></br>
          <strong data-testid="ob-emailfunnel-email">{targetEmail ?? 'dummymail@mail.com'}</strong>
        </InstructionText>
        <VSpace $h={32} />

        <div
          style={{ width: '100%', display: 'flex', gap: '4px', flexDirection: 'column' }}
          data-testid="ob-emailfunnel-code-wrap"
        >
          <FloatingInput
            id="verification_code"
            name="verification_code"
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value?.replace(/\D/g, ''))} // digits only
            disabled={loading}
            autoFocus
            placeholder=" "
            spellCheck={false}
            required
            minLength={6}
            maxLength={6}
            numericOnly
            title="Enter the 6-digit code"
            autoComplete="one-time-code"
            data-testid="ob-emailfunnel-code"
          />
        </div>

        {errorMsg && (
          <div>
            <VSpace $h={8} />
            <ErrorText data-testid="ob-emailfunnel-error">{errorMsg}</ErrorText>
          </div>
        )}

        <VSpace $h={16} />

        <VerifyButton
          onClick={handleVerify}
          disabled={buttonsDisabled || !codeReady}
          aria-disabled={buttonsDisabled || !codeReady}
          data-testid="ob-emailfunnel-verify"
        >
          {loading ? <UniversalSpinner /> : 'Verify email'}
        </VerifyButton>

        <VSpace $h={32} />

        {secondsLeft === 0 ? (
          <ResendLink
            onClick={handleResend}
            aria-disabled={buttonsDisabled}
            data-testid="ob-emailfunnel-resend"
          >
            Resend code
          </ResendLink>
        ) : (
          <TimerText data-testid="ob-emailfunnel-timer">
            Resend available in {secondsLeft}s
          </TimerText>
        )}

        <VSpace $h={32} />
        <SkipButton onClick={goPrev} data-testid="ob-checkout-back">
          Go back
        </SkipButton>
      </div>
    </ContentSection>
  );
};

(VerifyEmail as any).progress = false;
(VerifyEmail as any).smallLogo = true;
